import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, getCurrentTenantUserId } from '../../api/client';
import { useNotifications } from '../../context/NotificationContext';
import { sendTypingIndicator } from '../../services/socket';
import './ConversationView.css';

interface Message {
  _id: string;
  conversation: string;
  sender: { _id: string; name: string };
  content: string;
  createdAt: string;
}

interface Participant {
  _id: string;
  name: string;
  email: string;
}

interface Conversation {
  _id: string;
  participants: Participant[];
  lastMessage: {
    content: string;
    sender: string;
    timestamp: string;
  } | null;
  updatedAt: string;
}

interface ConversationViewProps {
  conversation: Conversation;
}

const ConversationView: React.FC<ConversationViewProps> = ({ conversation }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { onNewMessage, onTyping, markDmRead } = useNotifications();

  // Compare against the TENANT user id — message.sender._id is a tenant users.id,
  // not the global account id.
  const getCurrentUserId = () => getCurrentTenantUserId();

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await apiClient.get<{ messages: Message[]; conversation: any }>(
        `/api/messages/conversations/${conversation._id}`
      );

      setMessages(data.messages);
      setLoading(false);
    } catch (err: any) {
      console.error('Failed to fetch messages:', err);
      setLoading(false);
    }
  }, [conversation._id]);

  useEffect(() => {
    fetchMessages();
    markDmRead(conversation._id);
  }, [fetchMessages, markDmRead, conversation._id]);

  // Listen for new messages
  useEffect(() => {
    const unsubscribe = onNewMessage((message) => {
      if (message.conversation === conversation._id) {
        setMessages(prev => [...prev, message]);
      }
    });

    return unsubscribe;
  }, [onNewMessage, conversation._id]);

  // Listen for typing indicator
  useEffect(() => {
    const unsubscribe = onTyping((data) => {
      if (data.conversationId === conversation._id) {
        const other = conversation.participants.find(p => p._id === data.userId);
        if (other) {
          setTypingUser(other.name);

          // Clear previous timeout
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }

          // Clear typing indicator after 3 seconds
          typingTimeoutRef.current = setTimeout(() => {
            setTypingUser(null);
          }, 3000);
        }
      }
    });

    return () => {
      unsubscribe();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [onTyping, conversation._id, conversation.participants]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim() || sending) return;

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage('');

    try {
      const { data } = await apiClient.post<{ message: Message; conversation: any }>(
        `/api/messages/conversations/${conversation._id}/messages`,
        { content: messageContent }
      );

      setMessages(prev => [...prev, data.message]);
    } catch (err: any) {
      console.error('Failed to send message:', err);
      // Restore the message if sending failed
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    sendTypingIndicator(conversation._id);
  };

  const formatTime = (timestamp: string): string => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateHeader = (timestamp: string): string => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t('messaging.today');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('messaging.yesterday');
    } else {
      return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
    }
  };

  const shouldShowDateHeader = (current: Message, previous: Message | undefined): boolean => {
    if (!previous) return true;
    const currentDate = new Date(current.createdAt).toDateString();
    const previousDate = new Date(previous.createdAt).toDateString();
    return currentDate !== previousDate;
  };

  const userId = getCurrentUserId();

  if (loading) {
    return (
      <div className="conversation-view__loading">
        <div className="conversation-view__spinner"></div>
      </div>
    );
  }

  return (
    <div className="conversation-view">
      <div className="conversation-view__messages">
        {messages.length === 0 ? (
          <div className="conversation-view__empty">
            <p>{t('messaging.startConversation')}</p>
          </div>
        ) : (
          messages.map((message, index) => (
            <React.Fragment key={message._id}>
              {shouldShowDateHeader(message, messages[index - 1]) && (
                <div className="conversation-view__date-header">
                  {formatDateHeader(message.createdAt)}
                </div>
              )}
              <div
                className={`conversation-view__message ${
                  message.sender._id === userId ? 'conversation-view__message--sent' : 'conversation-view__message--received'
                }`}
              >
                <div className="conversation-view__bubble">
                  <p className="conversation-view__text">{message.content}</p>
                  <span className="conversation-view__time">{formatTime(message.createdAt)}</span>
                </div>
              </div>
            </React.Fragment>
          ))
        )}
        {typingUser && (
          <div className="conversation-view__typing">
            {t('messaging.typing', { name: typingUser })}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form className="conversation-view__input" onSubmit={handleSubmit}>
        <input
          type="text"
          value={newMessage}
          onChange={handleInputChange}
          placeholder={t('messaging.placeholder')}
          disabled={sending}
        />
        <button type="submit" disabled={sending || !newMessage.trim()}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default ConversationView;
